import { useEffect, useState, useCallback, useRef } from "react";
import {
	Title,
	Button,
	Group,
	Container,
	SimpleGrid,
	Text,
	createStyles, Divider, Skeleton,
} from "@mantine/core";
import { getMovie } from "../api/api-movies"
import { NavLink } from "react-router-dom";
import { CardMovieList } from "../components/organisms/CardMovieList"
import { useAppDispatch, useAppSelector, useInfiniteScroll } from "../utils/hooks"
import { sortMoviesBy } from "../utils/utils"
import { setWatchlistOrder, setWatchlistRecentlyViewedOrder } from "../store/reducers/general"
import { useMediaQuery } from "@mantine/hooks"
import ls from 'localstorage-slim';

interface Props {
  variant?: string
  title: string
}

export function Watchlist({ variant, title }: Props) {

  const ITEMS_PER_PAGE = 12
  const storageKey = `watchlist_scroll_${variant || 'default'}`
  const scrollYRef = useRef(0);
  const pendingScrollRef = useRef<number | null>(null);

  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [movieList, setMovieList] = useState<any[] | undefined>(undefined)

  const movieListState = useAppSelector((state) => state.movieList)
  const generalSettings = useAppSelector((state) => state.general)
  const dispatch = useAppDispatch()

  const useStyles = createStyles((theme) => ({
    root: {
      paddingTop: 80,
      paddingBottom: 80,
    },

    control: {
      [theme.fn.smallerThan('sm')]: {
        width: '100%',
      },
    },

    mobileImage: {
      [theme.fn.largerThan('sm')]: {
        display: 'none',
      },
    },

    desktopImage: {
      [theme.fn.smallerThan('sm')]: {
        display: 'none',
      },
    },
  }))

  const { classes } = useStyles()
  const smallScreen = useMediaQuery('(max-width: 768px)')

  const orderDefault = (variant === 'recently-viewed')
    ? generalSettings.watchlistRecentlyViewedOrder
    : generalSettings.watchlistOrder

  const orderDefaultValues = orderDefault ? orderDefault.split("|") : ["date", "desc"]
  const sortBy = orderDefaultValues[0]
  const sortOrder = orderDefaultValues[1]

  // Aggiorna il ref ad ogni scroll (ref non ha problemi di closure)
  useEffect(() => {
    const handleScroll = () => {
      scrollYRef.current = window.scrollY;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch iniziale
  useEffect(() => {
    if (movieListState.movieList) {
      setIsLoading(true)

      const fetchInitialMovies = async () => {
        try {
          // Filtra l'intera lista in base a visti / non visti
          const allFilteredMovies = movieListState.movieList!.filter(
            (movie) => movie.watched === (variant === "recently-viewed")
          )

          // Controlla se c'è uno stato salvato per ripristinare lo scroll
          const saved: any = ls.get(storageKey);
          let initialLoadCount = ITEMS_PER_PAGE;

          if (saved && saved.totalCount === allFilteredMovies.length) {
            initialLoadCount = Math.max(saved.loadedCount, ITEMS_PER_PAGE);
            pendingScrollRef.current = saved.scrollY;
          }
          ls.remove(storageKey);

          // Prendo i film da fetchare
          const moviesToFetch = allFilteredMovies.slice(0, initialLoadCount)

          // Faccio il get movie solo di quelli che mi servono
          const currentMovieList = await Promise.all(
            moviesToFetch.map(async (movie) => {
              const movieFetched = await getMovie(parseInt(movie.id));
              return {
                ...movieFetched,
                added_on: movie.added_on,
                watched_on: movie.watched_on,
              }
            })
          )

          // Ordina solo i film fetchati
          const movieListSorted = sortMoviesBy(currentMovieList, sortBy, sortOrder)

          setMovieList(movieListSorted)

        } catch (error) {
          console.error("Errore nel caricamento dei film:", error);
        } finally {
          setIsLoading(false)
        }
      };

      fetchInitialMovies();
    }
  }, [movieListState.movieList, variant, sortBy, sortOrder]);

  // Caricamento di altri film (infinite scroll)
  const loadMore = useCallback(async () => {
    if (!movieListState.movieList || isLoadingMore) return;

    setIsLoadingMore(true);
    try {
      const allFilteredMovies = movieListState.movieList.filter(
        (movie) => movie.watched === (variant === 'recently-viewed')
      );

      const startIndex = movieList?.length || 0;
      const endIndex = startIndex + ITEMS_PER_PAGE;
      const moviesToFetch = allFilteredMovies.slice(startIndex, endIndex);

      if (moviesToFetch.length === 0) {
        setIsLoadingMore(false);
        return;
      }

      const newMovies = await Promise.all(
        moviesToFetch.map(async (movie) => {
          const movieFetched = await getMovie(parseInt(movie.id));
          return {
            ...movieFetched,
            added_on: movie.added_on,
            watched_on: movie.watched_on,
          };
        })
      );

      const updatedList = [...(movieList || []), ...newMovies];
      const movieListSorted = sortMoviesBy(updatedList, sortBy, sortOrder);
      setMovieList(movieListSorted);
    } catch (error) {
      console.error("Errore nel caricamento dei film:", error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [movieListState.movieList, movieList, variant, sortBy, sortOrder, isLoadingMore]);

  const totalMoviesCount = movieListState.movieList
    ? movieListState.movieList.filter(movie => movie.watched === (variant === 'recently-viewed')).length
    : 0;

  const hasMore = movieList ? movieList.length < totalMoviesCount : false;

  const sentinelRef = useInfiniteScroll(loadMore, hasMore && !isLoadingMore);

  // Ripristina lo scroll DOPO che React ha renderizzato i nuovi film nel DOM
  useEffect(() => {
    if (pendingScrollRef.current !== null && movieList && movieList.length > 0) {
      const scrollTo = pendingScrollRef.current;
      pendingScrollRef.current = null;
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollTo);
      });
    }
  }, [movieList]);

  // Salva in localStorage all'unmount usando il ref (che ha il valore reale,
  // non quello resettato da ScrollToTop)
  useEffect(() => {
    return () => {
      if (movieList && movieList.length > 0) {
        ls.set(storageKey, {
          loadedCount: movieList.length,
          scrollY: scrollYRef.current,
          totalCount: totalMoviesCount,
        });
      }
    };
  }, [movieList, totalMoviesCount]);

  const sortMovies = (value: string | null) => {
    if (value && movieList) {

      // Aggiorno lo store redux per mantenere salvati i settaggi della select
      if (variant === 'recently-viewed') {
        dispatch(setWatchlistRecentlyViewedOrder(value));
      } else {
        dispatch(setWatchlistOrder(value));
      }

      const values = value.split('|');
      const by = values[0];
      const order = values[1];

      const oldMovieList = [...movieList];
      const movieListSorted = sortMoviesBy(oldMovieList, by, order)

      setMovieList(movieListSorted)
    }
  }

  const selectSortOptions = () => {

    const optionsDefault = [
      // {value: 'date|asc', label: 'Data di uscita crescente'},
      { value: 'date|desc', label: 'Data di uscita' }
    ];

    return (variant === 'recently-viewed') ?
      [
        // {value: 'watchedOn|asc', label: 'Data di visualizzazione crescente'},
        { value: 'watchedOn|desc', label: 'Data di visualizzazione' },
        ...optionsDefault
      ]
      :
      [
        // {value: 'addedOn|asc', label: 'Data di aggiunta crescente'},
        { value: 'addedOn|desc', label: 'Data di aggiunta' },
        ...optionsDefault
      ]
  }

  return (
    <>

      {
        (movieList && movieList.length > 0) ?
          <>
            <Container mb="xl">

              <Group position="apart" align="center">
                <Title order={4}>{title}</Title>
                <Button variant="outline" component={NavLink} to="/movie/search">Aggiungi film</Button>
              </Group>

              <Divider my="sm" variant="dotted"/>

              <Group position="apart" align="center">
                <Text size="sm">{totalMoviesCount} film</Text>
                {/*<Select*/}
                {/*  size="xs"*/}
                {/*  onChange={(value) => sortMovies(value)}*/}
                {/*  value={variant === 'recently-viewed' ? generalSettings.watchlistRecentlyViewedOrder : generalSettings.watchlistOrder}*/}
                {/*  data={selectSortOptions()}*/}
                {/*/>*/}
              </Group>

            </Container>

            <CardMovieList
              movieList={movieList}
              isLoading={isLoading}
              mobileView="compact"
            />

            {/* Scheletri per il caricamento di più film */}
            {isLoadingMore && (
              <Container mt="md">
                <SimpleGrid cols={smallScreen ? 2 : 4}>
                  {
                    [...Array(4)].map((item: number, i: number) => (
                      <Skeleton key={i} height={smallScreen ? 200 : 360}/>
                    ))
                  }
                </SimpleGrid>
              </Container>
            )}

            {/* Sentinel per l'infinite scroll */}
            {hasMore && <div ref={sentinelRef} style={{ height: 1 }}/>}
          </>
          : (movieList && movieList.length === 0) ?
            <Container style={{ textAlign: 'center' }}>
              <SimpleGrid>
                <div>
                  {
                    !variant ?
                      <>
                        <Title order={1}>Nessun film nella tua watchlist</Title>
                        <Text color="dimmed" size="lg">
                          Ancora non hai aggiunto nessun film alla watchlist.
                        </Text>
                        <Button
                          component={NavLink}
                          to="/movie/search"
                          variant="outline"
                          size="md"
                          mt="xl"
                          className={classes.control}
                        >
                          Aggiungi un nuovo film
                        </Button>
                      </>
                      :
                      <>
                        <Title order={1}>Nessun film in questa lista</Title>
                        <Text color="dimmed" size="lg">
                          Non ci sono film nella lista selezionata.
                        </Text>
                        <Button
                          component={NavLink}
                          to="/watchlist"
                          variant="outline"
                          size="md"
                          mt="xl"
                          className={classes.control}
                        >
                          Vai alla tua watchlist
                        </Button>
                      </>
                  }
                </div>
              </SimpleGrid>
            </Container>
            :
            <Container>
              <SimpleGrid cols={smallScreen ? 2 : 4}>
                {
                  [...Array(8)].map((item: number, i: number) => (
                    <Skeleton key={i} height={smallScreen ? 200 : 360}/>
                  ))
                }
              </SimpleGrid>
            </Container>
      }
    </>
  )

}
